/**
 * Die Leash des Heroes (GAME_DESIGN Abschnitt 5a).
 *
 * Nach vorn endet sein erlaubter Bereich ein Stueck vor seiner eigenen
 * Frontline. **Die Army kontrolliert den Raum, in dem der Hero kaempfen
 * darf** - das ist das wichtigste Unterscheidungsmerkmal zu einem MOBA.
 *
 * Drei Dinge weichen die Regel bewusst auf:
 *
 * 1. Ohne Armee gilt eine Grundstrecke ab dem eigenen Anchor. Sonst klebte
 *    der Hero in jeder Pause zwischen zwei Wellen fest.
 * 2. Erkaempfter Raum bleibt erkaempft, bis der Hero ihn selbst aufgibt
 *    (der Nachhall, siehe unten).
 * 3. Wer vom gegnerischen HERO getroffen wird, darf eine kurze Zeit lang
 *    frei nachsetzen (Retaliation Window). Ohne das koennte ein
 *    Fernkaempfer hinter seiner Front stehen und einen Nahkaempfer an
 *    dessen Leash risikolos beschiessen.
 *
 * Und sie kommt nur mit eigenem Tempo ZURUECK (10.10.2026): Der Hero
 * kann ihr davonlaufen, der Nebel kommt nach. Vorher hing sie beim
 * Rueckzug starr an ihm und sprang in seinem Lauftempo.
 *
 * Gerechnet wird in x, nicht im Fortschritt: Die Bewegungsgrenzen des Heroes
 * sind es auch (HERO_BOUNDS), und beide muessen zusammenpassen.
 *
 * Diese Datei wird von BEIDEN Seiten benutzt - der Server rechnet die
 * verbindliche Position, der Client sagt dieselbe voraus. Es darf nur eine
 * Definition davon geben, wie weit ein Hero darf.
 */

import {
  HERO_FRONTLINE_ZONE_DEPTH,
  HERO_LEASH_BASE_DISTANCE,
  HERO_LEASH_ADVANCE_SPEED,
  HERO_LEASH_BRAKE_ZONE,
  HERO_LEASH_RETREAT_SPEED,
} from '@dueline/game-data';

/**
 * Was eine Runde der Leash ergibt - **zwei** Zahlen, und sie sind nicht
 * dasselbe.
 *
 * Am 12.10.2026 ist genau diese Verwechslung aufgefallen: Der Aufrufer hat
 * sich die Grenze EINSCHLIESSLICH Retaliation gemerkt, und im naechsten
 * Takt kam der Zuschlag noch einmal obendrauf. Zwanzigmal je Sekunde. Nach
 * drei Sekunden Fenster stand die Grenze bei 52 Prozent der Lane statt bei
 * 21 - die Leash war praktisch ausgeschaltet.
 *
 * Deshalb gibt die Regel beide Zahlen heraus, statt sich darauf zu
 * verlassen, dass der Aufrufer die richtige nimmt.
 */
export interface LeashErgebnis {
  /** So weit darf der Hero JETZT - einschliesslich Retaliation. */
  grenze: number;
  /**
   * Das gehoert ins Gedaechtnis - OHNE Retaliation.
   *
   * Der Zuschlag gilt fuer den Moment, nicht fuer immer. Wer ihn merkt,
   * baut eine Ratsche.
   */
  fuersGedaechtnis: number;
}

export interface LeashEingabe {
  /** x des eigenen Anchors. */
  anchorX: number;
  /** In welche Richtung der Gegner liegt: +1 oder -1. */
  vorwaerts: 1 | -1;
  /** x der eigenen Frontline. null heisst: keine Armee draussen. */
  frontlineX: number | null;
  /** Wo der Hero gerade steht - fuer den Nachhall. */
  heroX: number;
  /**
   * Die Grenze aus dem letzten Takt. null beim allerersten Mal.
   *
   * Sie ist das Gedaechtnis der Regel: Ohne sie wuesste niemand mehr,
   * welchen Raum dieser Hero schon einmal gehalten hat.
   */
  gemerkt: number | null;
  /** Laeuft gerade ein Retaliation Window? */
  retaliation: boolean;
  /**
   * Die GELTENDE Grenze des letzten Takts - null beim allerersten Mal.
   *
   * Gebraucht, damit auch der Rueckweg aus dem Retaliation-Fenster
   * gleitet statt zu springen. "gemerkt" allein reicht dafuer nicht: Dort
   * steht der Stand OHNE das Fenster, und genau der Unterschied ist es,
   * der sonst schlagartig verschwindet.
   */
  gemerkteGrenze: number | null;

  /**
   * Dauer dieses Takts in Sekunden - fuer das Tempo des Rueckwegs.
   *
   * Damit ist diese Regel NICHT mehr doppelt aufrufbar: Wer sie zweimal
   * je Takt rechnet, laesst die Grenze doppelt so schnell zurueckkommen.
   * Bis zum 10.10.2026 war das erlaubt und ausdruecklich vermerkt.
   */
  dt: number;
}

/**
 * Der am weitesten vorn liegende x-Wert, den dieser Hero erreichen darf -
 * die VORDERKANTE seiner Kampfzone.
 *
 * Die Zone liegt mittig auf der Frontline: HERO_FRONTLINE_ZONE_DEPTH davor
 * und ebenso viel dahinter. Der Hero darf sie ganz betreten und wird an der
 * Vorderkante aufgehalten - er steht damit nie hinter seinen eigenen Leuten
 * (Rueckmeldung aus dem Spiel vom 08.10.2026).
 *
 * Die Grundstrecke ab dem Anchor ist zugleich die UNTERGRENZE: Ist die
 * eigene Armee weit zurueckgedraengt, stellt sie den Hero nicht enger als
 * gar keine Armee. Sonst waere eine zusammengebrochene Welle schlimmer als
 * keine - und genau dann braucht man seinen Hero.
 *
 * DER NACHHALL (Produktentscheidung vom 08.10.2026)
 *
 * Nach vorn schiebt die Grenze nur die Armee. Zurueck springt sie nicht
 * von allein: Faellt die Armee, bleibt der erkaempfte Raum stehen. Der
 * Hero kann dort weiterkaempfen, aber keinen Schritt weiter vor - und
 * erst wenn er selbst zurueckgeht, kommt die Grenze mit.
 *
 * Vorher sprang sie in dem Moment zurueck, in dem der letzte eigene Creep
 * fiel. Im Spiel hiess das: Man kaempft sich mit seiner Welle nach vorn,
 * die Welle stirbt - und man steht ploetzlich weit vor einer Grenze, die
 * man gerade noch selbst gehalten hat.
 *
 * Der Nachhall ist an den Hero gebunden, nicht an die Uhr: Er reicht nie
 * weiter als eine Zonentiefe vor ihn. Wer sich zurueckzieht, gibt den Raum
 * damit wirklich auf - zurueckholen kann ihn nur eine neue Armee.
 */
export function leashGrenze(eingabe: LeashEingabe): LeashErgebnis {
  const grund = eingabe.anchorX + eingabe.vorwaerts * HERO_LEASH_BASE_DISTANCE;

  const ausFrontline =
    eingabe.frontlineX === null
      ? grund
      : eingabe.frontlineX + eingabe.vorwaerts * HERO_FRONTLINE_ZONE_DEPTH;

  /*
   * Der Nachhall: die alte Grenze, aber hoechstens eine Zonentiefe vor dem
   * Hero. Steht er weit dahinter, haelt er diesen Raum nicht mehr - dann
   * schrumpft der Nachhall sofort auf das, was er wirklich deckt.
   */
  const nachhall =
    eingabe.gemerkt === null
      ? grund
      : weiterVorn(
          grund,
          weiterHinten(
            eingabe.gemerkt,
            eingabe.heroX + eingabe.vorwaerts * HERO_FRONTLINE_ZONE_DEPTH,
            eingabe.vorwaerts,
          ),
          eingabe.vorwaerts,
        );

  const vorn = weiterVorn(
    weiterVorn(grund, ausFrontline, eingabe.vorwaerts),
    nachhall,
    eingabe.vorwaerts,
  );

  /*
   * Erst daempfen, DANN den Zuschlag draufgeben - und nur die gedaempfte
   * Zahl merken.
   *
   * Andersherum (und so war es bis zum 12.10.2026) wandert der Zuschlag
   * ins Gedaechtnis und waechst mit jedem Takt weiter.
   */
  const fuersGedaechtnis = gedaempft(eingabe.gemerkt, vorn, eingabe.vorwaerts, eingabe.dt);

  /*
   * Waehrend des Fensters haelt die Grenze NICHT auf.
   *
   * Sie bleibt dem Hero immer eine Zonentiefe voraus - er laeuft also, so
   * weit die Beine tragen. Begrenzt wird durch die ZEIT des Fensters, nicht
   * durch eine Strecke (Produktentscheidung vom 12.10.2026).
   *
   * Zwei andere Fassungen waren vorher falsch: eine feste Strecke von sechs
   * Einheiten erreichte keinen Fernkaempfer, und "bis zum Angreifer" traf
   * zwar den Zweck, fuehlte sich im Spiel aber falsch an.
   *
   * Entscheidend ist, was NICHT passiert: Der gewonnene Raum geht nicht ins
   * Gedaechtnis. Wer vorrueckt, steht nach dem Fenster weit draussen ohne
   * Armee und muss zurueck - genau das fehlte der ersten Fassung, und sie
   * war damit ein Trick statt einer Entscheidung.
   */
  const ziel = eingabe.retaliation
    ? weiterVorn(
        fuersGedaechtnis,
        eingabe.heroX + eingabe.vorwaerts * HERO_FRONTLINE_ZONE_DEPTH,
        eingabe.vorwaerts,
      )
    : fuersGedaechtnis;

  /*
   * Die angezeigte Grenze gleitet in BEIDE Richtungen (12.10.2026).
   *
   * Zurueck: Schliesst das Fenster, faellt das Ziel schlagartig auf die
   * Linie der eigenen Armee. Ungebremst sprang der Nebel im Bild - "das
   * wirkt unnatuerlich".
   *
   * Nach vorn: Oeffnet das Fenster, liegt das Ziel schlagartig eine ganze
   * Zonentiefe weiter vorn - "das springt jetzt so weg von mir". Darum
   * auch hier ein Tempo, nur ein deutlich hoeheres.
   *
   * Das GEDAECHTNIS oben bleibt davon unberuehrt: Dort schiebt die Armee,
   * und die ist schon gedaempft. Eine zweite Bremse davor waere nur
   * traege - und wuerde das Vorruecken der eigenen Welle verschleppen.
   */
  const grenze = gedaempft(
    eingabe.gemerkteGrenze,
    ziel,
    eingabe.vorwaerts,
    eingabe.dt,
    HERO_LEASH_ADVANCE_SPEED,
  );

  return { grenze, fuersGedaechtnis };
}

/**
 * Laesst eine Zahl ihrem Ziel mit eigenem Tempo nachlaufen, statt zu
 * springen.
 *
 * ZURUECK immer gebremst: Vorher hing die Grenze starr am Hero und sprang
 * damit in seinem Lauftempo - im Bild klebte der Nebel an seinen Fersen
 * (Rueckmeldung vom 10.10.2026).
 *
 * NACH VORN je nach Aufrufer, siehe "vorwaertsTempo".
 *
 * Dass der Hero dabei weit hinter der Grenze steht, ist unbedenklich:
 * Sie begrenzt nur den Weg nach VORNE. Sie kann ihn also nie einholen
 * und nie einsperren.
 */
function gedaempft(
  gemerkt: number | null,
  ziel: number,
  vorwaerts: 1 | -1,
  dt: number,
  /*
   * Unendlich heisst: nach vorn sofort.
   *
   * So gilt es fuer das GEDAECHTNIS - dort schiebt die Armee, und die ist
   * schon gedaempft (ziehFrontlineNach). Nur die angezeigte Grenze gleitet
   * auch nach vorn, damit die Spruenge des Retaliation-Fensters weich
   * werden (12.10.2026).
   */
  vorwaertsTempo = Number.POSITIVE_INFINITY,
): number {
  // Beim allerersten Takt gibt es nichts nachzuziehen.
  if (gemerkt === null) return ziel;

  const weg = (ziel - gemerkt) * vorwaerts;
  if (weg === 0) return ziel;

  // Nach vorn so schnell, wie der Aufrufer es erlaubt. Zurueck immer
  // mit der einen langsamen Geschwindigkeit.
  const tempo = weg > 0 ? vorwaertsTempo : HERO_LEASH_RETREAT_SPEED;
  const schritt = tempo * Math.max(0, dt);
  if (Math.abs(weg) <= schritt) return ziel;

  return gemerkt + vorwaerts * (weg > 0 ? schritt : -schritt);
}

/** Der in Laufrichtung weiter vorn liegende der beiden Werte. */
function weiterVorn(a: number, b: number, vorwaerts: 1 | -1): number {
  return (b - a) * vorwaerts > 0 ? b : a;
}

/** Der in Laufrichtung weiter hinten liegende der beiden Werte. */
function weiterHinten(a: number, b: number, vorwaerts: 1 | -1): number {
  return (b - a) * vorwaerts < 0 ? b : a;
}

/**
 * Wie viel Tempo NACH VORNE der Hero an dieser Stelle noch hat: 1 weit weg,
 * 0 an der Grenze.
 *
 * Damit faellt die Bewegung auf den letzten Metern weich ab, statt gegen
 * eine unsichtbare Wand zu laufen (Produktentscheidung vom 08.10.2026).
 * Nach hinten und zur Seite greift die Bremse nie - wer zurueck will, darf
 * das immer sofort.
 */
export function bremsFaktor(x: number, grenze: number, vorwaerts: 1 | -1): number {
  if (HERO_LEASH_BRAKE_ZONE <= 0) return 1;

  // Wie weit ist es noch bis zur Grenze, in Laufrichtung gemessen?
  const rest = (grenze - x) * vorwaerts;
  if (rest >= HERO_LEASH_BRAKE_ZONE) return 1;
  if (rest <= 0) return 0;
  return rest / HERO_LEASH_BRAKE_ZONE;
}
